import i18n from '@/shared/i18n/config'
import { toast } from '@workspace/ui/components/Sonner'
import { AxiosError } from 'axios'

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Validation constraint from 422 Unprocessable Entity errors
 * Backend format: HttpExceptionFilter (bad-request.filter.ts)
 */
interface ValidationConstraint {
    property: string
    children: ValidationConstraint[]
    constraints: Record<string, string>
}

/**
 * API error response structure supporting all 3 backend error formats:
 * - Case 1: Validation Errors (422) - message is ValidationConstraint[]
 * - Case 2: Database Errors (409/500) - message is string like "error.unique.email"
 * - Case 3: HTTP Exceptions (404/401/403) - message is string like "Not Found"
 */
interface ApiErrorResponse {
    message: string | ValidationConstraint[]
    error?: string
    statusCode?: number
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if the message is a validation error (array of constraints)
 */
function isValidationErrorMessage(message: unknown): message is ValidationConstraint[] {
    if (!Array.isArray(message)) return false
    if (message.length === 0) return false

    // Check if first element has the expected structure
    const first = message[0]
    if (typeof first !== 'object' || first === null || !('property' in first)) return false

    // It should have either constraints or children
    return 'constraints' in first || 'children' in first
}

// =============================================================================
// Error Handlers
// =============================================================================

/**
 * Extracts the translation key from an error message
 * Converts "error.userMissingProjectOperatePermission" to "error.userMissingProjectOperatePermission"
 */
function getTranslationKey(message: string): string {
    // If message starts with "error.", use it directly
    if (message.startsWith('error.')) {
        return message
    }
    // Otherwise, prefix with "error."
    return `error.${message}`
}

/**
 * Handle Validation Errors (Case 1 - 422 Unprocessable Entity)
 * Extracts constraint messages from nested structure and translates them
 *
 * @returns Array of translated error messages for each field
 *
 * @example
 * Input:
 * [{ property: "email", constraints: { isNotEmpty: "error.fields.is_not_empty" }}]
 *
 * Output:
 * ["email: Trường này không được để trống"]
 */
function extractValidationErrors(constraints: ValidationConstraint[]): string[] {
    const errors: string[] = []

    function extractFromConstraint(constraint: ValidationConstraint, parentPath: string = '') {
        const fieldPath = parentPath ? `${parentPath}.${constraint.property}` : constraint.property

        // Extract constraint messages
        if (constraint.constraints && Object.keys(constraint.constraints).length > 0) {
            for (const [, value] of Object.entries(constraint.constraints)) {
                const translationKey = getTranslationKey(value)
                let translatedMessage = i18n.t(translationKey)

                // If translation not found, use original message
                if (translatedMessage === translationKey) {
                    translatedMessage = value
                }

                errors.push(translatedMessage)
            }
        }

        // Recursively process children
        if (constraint.children && constraint.children.length > 0) {
            for (const child of constraint.children) {
                extractFromConstraint(child, fieldPath)
            }
        }
    }

    for (const constraint of constraints) {
        extractFromConstraint(constraint)
    }

    return errors
}

/**
 * Handle string message errors (Case 2 & 3)
 * - Case 2: Database errors like "error.unique.email"
 * - Case 3: HTTP exceptions like "Not Found"
 */
function handleStringMessage(message: string, statusCode?: number): string {
    // Get translation key
    const translationKey = getTranslationKey(message)

    // Try to get translated message
    let translatedMessage = i18n.t(translationKey)

    // If translation returns the key itself, use fallback
    if (translatedMessage === translationKey) {
        translatedMessage = message || i18n.t('error.generic')
    }

    return translatedMessage
}

/**
 * Get fallback message based on HTTP status code
 */
function getStatusCodeFallback(statusCode?: number): string {
    switch (statusCode) {
        case 400:
            return 'error.badRequest'
        case 401:
            return 'error.unauthorized'
        case 403:
            return 'error.forbidden'
        case 404:
            return 'error.notFound'
        case 500:
            return 'error.internalServerError'
        default:
            return 'error.generic'
    }
}

// =============================================================================
// Main Export
// =============================================================================

/**
 * Handles API errors and shows appropriate toast messages
 *
 * Supports 3 error formats:
 * - Case 1: Validation Errors (422) - shows first validation error
 * - Case 2: Database Errors (409/500) - shows translated message
 * - Case 3: HTTP Exceptions (404/401/403) - shows translated message
 */
export function handleApiError(error: unknown): void {
    const axiosError = error as AxiosError<ApiErrorResponse>
    const errorData = axiosError.response?.data
    const statusCode = axiosError.response?.status

    const message = errorData?.message

    let displayMessage: string

    // Case 1: Validation Errors (422) - array of constraints
    if (isValidationErrorMessage(message)) {
        const validationErrors = extractValidationErrors(message)
        // Show first error for toast, user can check form for all errors
        displayMessage = validationErrors[0] || i18n.t('error.generic')
    }
    // Case 2 & 3: String message or fallback
    else if (typeof message === 'string' && message) {
        // Map common NestJS default messages to standard camelCase i18n keys
        const nestJsDefaults: Record<string, string> = {
            'Forbidden resource': 'forbiddenResource',
            Forbidden: 'forbiddenResource',
        }

        const normalizedMessage = nestJsDefaults[message] || message
        displayMessage = handleStringMessage(normalizedMessage, statusCode)
    }
    // No message - use status code fallback
    else {
        const fallbackKey = getStatusCodeFallback(statusCode)
        displayMessage = i18n.t(fallbackKey)
    }

    toast.error({
        title: displayMessage,
    })
}
